import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  alpha,
  Divider,
} from "@mui/material";
import IDTier2Done from "./IDTier2Done";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import axios from "axios";
import InfoPopUp from "../../Common/InfoPopUp";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../Common/Buttons";
import InfoIcon from "@mui/icons-material/Info";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const Tier3Document = ({ userDetails, steps, setIsloading }) => {
  const { btncolor } = useSelector((state) => state);
  const theme = localStorage.getItem("theme");
  const homeCurrencySymbol = localStorage.getItem("homeCurrencySymbol");
  const [month, day, year] = userDetails?.dob?.split("-") || "12-12-2000";
  const [show, setShow] = useState(false);
  const [nextStep, setNextStep] = useState(false);
  const [isDisable, setIsDisable] = useState(true);
  const [tier3Flag, setTier3flag] = useState(
    userDetails?.userTierDocsStatus ? true : false
  );
  const [bankStateMent, setBankStateMent] = useState(null);
  const [taxNumber, setTaxNumber] = useState("");
  const [taxStateMent, setTaxStateMent] = useState(null);
  const exchangeFeesDomain = `/exchange-fees`;
  const onClose = () => setShow(false);

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/jfif",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const maxFileSize = 6 * 1024 * 1024;
  console.log("isdisable", isDisable);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const { type, size, name } = selectedFile;
    const fileExtension = name.split(".").pop().toLowerCase();
    const validExtensions = ["jpg", "jpeg", "png", "pdf", "doc", "docx", "jfif"];

    if (!allowedTypes.includes(type) || !validExtensions.includes(fileExtension)) {
      showToast("error", "Please add a .JPG, .JPEG, .PNG, .PDF, .DOC, or .DOCX file.");
      setIsDisable(true);
      return;
    }

    if (size > maxFileSize) {
      showToast("error", "File size exceeds 6 MB limit.");
      setIsDisable(true);
      return;
    }

    setIsDisable(false);
  };

  const handleNext = () => {
    if (bankStateMent) {
      setNextStep(true);
    } else {
      showToast("error", "Please select bank statement");
    }
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append("userId", localStorage.getItem("userId"));
    formData.append("bankStatement", bankStateMent);
    formData.append("tin", taxNumber);
    formData.append("taxStatement", taxStateMent);
    formData.append("uuid", localStorage.getItem("uuid"));
    setIsloading(true);
    try {
      const response = await axios.post(
        `${WEBSERVICE}user/submitKyc`,
        formData,
        {
          headers: {
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      const result = response.data;

      if (result.error.error_data !== 0) {
        showToast("error", result.error.error_msg);
        setIsloading(false);
      } else {
        showToast("success", "Identity Verification Documents for Tier 3 Submitted");
        setIsloading(false);
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (error) {
      showToast("error", "Something went wrong. Please try again later.");
      console.error(error);
      setIsloading(false);
    }
  };

  // ─── helpers ────────────────────────────────────────────────────────────────

  // Renders a read-only display field card
  const FieldCard = ({ label, children, hint }) => (
    <Box sx={fieldCard}>
      <Typography sx={fieldCardLabel}>{label}</Typography>
      <Box sx={fieldCardValue}>{children}</Box>
      {hint && <Typography sx={fieldCardHint}>{hint}</Typography>}
    </Box>
  );

  // ─── Tier-2 individual fields ────────────────────────────────────────────────
  const individualFields = (
    <Grid container spacing={2}>
      {userDetails?.panNo && (
        <Grid size={{ xs: 12, md: 6 }}>
          <FieldCard
            label="PAN Number"
            hint="Only original PAN number's document's number is applicable."
          >
            <Typography sx={fieldCardText}>{userDetails?.panNo}</Typography>
          </FieldCard>
        </Grid>
      )}

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard
          label="Date of Birth (MM/DD/YYYY)"
          hint="Only government issued document's date of birth are applicable."
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            {[month, day, year].map((val, i) => (
              <Box key={i} sx={dobChip}>{val}</Box>
            ))}
          </Box>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard
          label="Social Security Number or Passport Number"
          hint="Please enter the last four (4) digits of your Social Security Number or your complete passport number."
        >
          <Typography sx={fieldCardText}>{userDetails?.ssn}</Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Place of Birth">
          <Typography sx={fieldCardText}>{userDetails?.birthPlace}</Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Industry">
          <Typography sx={fieldCardText}>{userDetails?.industry}</Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Occupation">
          <Typography sx={fieldCardText}>{userDetails?.occupation}</Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Source of Funds">
          <Typography sx={fieldCardText}>{userDetails?.sourcesOfFunds}</Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Transaction Volume">
          <Typography sx={fieldCardText}>
            {[
              "", `${homeCurrencySymbol}0 – 10,000`,
              `${homeCurrencySymbol}10,000 – ${homeCurrencySymbol}25,000`,
              `${homeCurrencySymbol}25,000 – ${homeCurrencySymbol}50,000`,
              `${homeCurrencySymbol}50,000 – ${homeCurrencySymbol}100,000`,
              `${homeCurrencySymbol}100,000 – ${homeCurrencySymbol}250,000`,
              `${homeCurrencySymbol}250,000 – ${homeCurrencySymbol}1,000,000`,
              `${homeCurrencySymbol}1,000,000+`,
            ][userDetails?.txnVolumeId] || "—"}
          </Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Annual Income">
          <Typography sx={fieldCardText}>
            {[
              "", `${homeCurrencySymbol}0 – 10,000`,
              `${homeCurrencySymbol}10,000 – ${homeCurrencySymbol}25,000`,
              `${homeCurrencySymbol}25,000 – ${homeCurrencySymbol}50,000`,
              `${homeCurrencySymbol}50,000 – ${homeCurrencySymbol}100,000`,
              `${homeCurrencySymbol}100,000 – ${homeCurrencySymbol}250,000`,
              `${homeCurrencySymbol}250,000 – ${homeCurrencySymbol}1,000,000`,
              `${homeCurrencySymbol}1,000,000+`,
            ][userDetails?.annualIncomeId] || "—"}
          </Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Net Worth">
          <Typography sx={fieldCardText}>
            {[
              "", `${homeCurrencySymbol}0 – 10,000`,
              `${homeCurrencySymbol}10,000 – ${homeCurrencySymbol}25,000`,
              `${homeCurrencySymbol}25,000 – ${homeCurrencySymbol}50,000`,
              `${homeCurrencySymbol}50,000 – ${homeCurrencySymbol}100,000`,
              `${homeCurrencySymbol}100,000 – ${homeCurrencySymbol}250,000`,
              `${homeCurrencySymbol}250,000 – ${homeCurrencySymbol}1,000,000`,
              `${homeCurrencySymbol}1,000,000+`,
            ][userDetails?.netWorthId] || "—"}
          </Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Employment Category">
          <Typography sx={fieldCardText}>{userDetails?.empCategory}</Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Employment Type">
          <Typography sx={fieldCardText}>{userDetails?.empType}</Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Politically Exposed Person (PEP)">
          <Typography sx={fieldCardText}>{userDetails?.pep === 1 ? "Yes" : "No"}</Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Purpose of the Account">
          <Typography sx={fieldCardText}>{userDetails?.accountPurpose}</Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Current Banking Partner">
          <Typography sx={fieldCardText}>{userDetails?.bankingPartner}</Typography>
        </FieldCard>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FieldCard label="Banking Relationship Duration">
          <Typography sx={fieldCardText}>{userDetails?.relationWithBank}</Typography>
        </FieldCard>
      </Grid>
    </Grid>
  );

  // ─── Tier-2 enterprise fields ────────────────────────────────────────────────
  const enterpriseFields = (
    <Grid container spacing={2}>
      {[
        { label: "Company Name", value: userDetails?.enterpriseUser?.companyName },
        { label: "Company Registration Number", value: userDetails?.enterpriseUser?.companyRegNo },
        { label: "Country of Incorporation", value: userDetails?.enterpriseUser?.companyregCountry },
        { label: "Company Website", value: userDetails?.enterpriseUser?.companyWebsite },
        { label: "Purpose of Account", value: userDetails?.enterpriseUser?.accountPurpose },
        { label: "Current Banking Partner", value: userDetails?.enterpriseUser?.bankingPartner },
        { label: "Banking Relationship Duration", value: userDetails?.enterpriseUser?.relationWithBank },
      ].map((f, i) => (
        <Grid key={i} size={{ xs: 12, md: 6 }}>
          <FieldCard label={f.label}>
            <Typography sx={fieldCardText}>{f.value || "—"}</Typography>
          </FieldCard>
        </Grid>
      ))}
    </Grid>
  );

  // ─── Tier-3 status badge ─────────────────────────────────────────────────────
  const tier3StatusBadge = () => {
    const s = userDetails?.userTierDocsStatus;
    if (!s) return null;
    const configs = {
      "0": { color: "#f59e0b", bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.25)", icon: <HourglassTopOutlinedIcon sx={{ fontSize: 16, color: "#f59e0b" }} />, text: "Tier 3 Identity verification documents submitted." },
      "2": { color: "#ef4444", bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.25)", icon: null, text: "Tier 3 Identity verification documents declined, please submit again." },
      "1": { color: "#4ade80", bg: "rgba(74,222,128,0.08)", border: "rgba(74,222,128,0.25)", icon: <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#4ade80" }} />, text: "Tier 3 Identity verification documents verified." },
    };
    const cfg = configs[s];
    if (!cfg) return null;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.6, borderRadius: "20px", backgroundColor: cfg.bg, border: `1px solid ${cfg.border}`, width: "fit-content" }}>
        {cfg.icon}
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: cfg.color }}>{cfg.text}</Typography>
      </Box>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={pageWrapper}>
      {/* Tier 2 Verified Hero Banner */}
      <IDTier2Done />

      {/* Tier 2 Info Card */}
      <Box sx={mainCard}>
        <Box sx={sectionHeader}>
          <Typography sx={sectionTitle}>Verified Information</Typography>
          <Typography sx={sectionCaption}>Your submitted identity details for Tier 2 verification</Typography>
        </Box>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 2.5 }} />

        {userDetails?.userType === 1 ? individualFields : enterpriseFields}

        {/* Begin Tier 3 CTA */}
        {!tier3Flag && !userDetails?.userTierDocsStatus && (
          <Box sx={{ mt: 3.5, pt: 2.5, borderTop: "1px solid rgba(125,125,125,0.13)" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                p: 2.5,
                borderRadius: "12px",
                backgroundColor: alpha(btncolor || "#1976d2", 0.06),
                border: `1px solid ${alpha(btncolor || "#1976d2", 0.2)}`,
                mb: 2,
              }}
            >
              <ShieldOutlinedIcon sx={{ color: btncolor, fontSize: 22, flexShrink: 0, mt: "2px" }} />
              <Box>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--text-color)", mb: 0.4 }}>
                  Unlock Tier 3 Verification
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "var(--text-color)", opacity: 0.65, lineHeight: 1.6 }}>
                  Tier 3 verification allows you to increase your Trading and Withdrawal limits. You can learn about transaction limits{" "}
                  <a href={exchangeFeesDomain} target="_blank" rel="noreferrer" style={{ color: btncolor, fontWeight: 600 }}>
                    here
                  </a>
                  . Navigate to the Daily Limit tab on that page.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ContainedButton variant="contained" onClick={() => setTier3flag(true)}>
                Begin Tier 3 Verification
              </ContainedButton>
              <InfoIcon
                sx={{ color: "var(--text-color)", opacity: 0.4, cursor: "pointer", fontSize: 20 }}
                onClick={() => setShow(true)}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Tier 3 Section ── */}
      {tier3Flag && (
        <Box sx={{ ...mainCard, mt: 2.5 }}>
          {/* Tier 3 Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: alpha(btncolor || "#1976d2", 0.12),
                  flexShrink: 0,
                }}
              >
                <LockOutlinedIcon sx={{ color: btncolor, fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={sectionTitle}>Identity Verification (Tier 3)</Typography>
                {tier3StatusBadge()}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 2.5 }} />

          {/* Tier 3 Form */}
          {userDetails?.userTierDocsStatus !== "1" && userDetails?.userTierDocsStatus !== "0" && (
            <Grid container spacing={2}>
              {!nextStep && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={uploadCard}>
                      <Box sx={uploadCardHeader}>
                        <UploadFileOutlinedIcon sx={{ fontSize: 20, color: btncolor }} />
                        <Typography sx={uploadCardTitle}>Bank Statement</Typography>
                      </Box>
                      <Typography sx={uploadCardHint}>
                        Accepted formats: .JPG, .JPEG, .PNG, .PDF, .DOC, .DOCX, .JFIF — max 6 MB
                      </Typography>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.jfif"
                        style={fileInputStyle}
                        onChange={(e) => {
                          setBankStateMent(e.target.files[0]);
                          handleFileChange(e);
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", alignItems: "center" }}>
                    <ContainedButton variant="contained" onClick={handleNext} disabled={isDisable}>
                      Next
                    </ContainedButton>
                  </Grid>
                </>
              )}

              {nextStep && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={fieldCard}>
                      <Typography sx={fieldCardLabel}>Tax Identification Number</Typography>
                      <input
                        type="text"
                        autoComplete="off"
                        maxLength="15"
                        placeholder="Enter Tax Identification Number"
                        onChange={(e) => setTaxNumber(e.target.value)}
                        onKeyDown={(e) => {
                          const regex = /^[a-zA-Z0-9 ]$/;
                          if (e.key.length === 1 && !regex.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => { e.preventDefault(); }}
                        style={modernInputStyle}
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={uploadCard}>
                      <Box sx={uploadCardHeader}>
                        <UploadFileOutlinedIcon sx={{ fontSize: 20, color: btncolor }} />
                        <Typography sx={uploadCardTitle}>Tax Statement</Typography>
                      </Box>
                      <Typography sx={uploadCardHint}>
                        Accepted formats: .JPG, .JPEG, .PNG, .PDF, .DOC, .DOCX, .JFIF — max 6 MB
                      </Typography>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.jfif"
                        style={fileInputStyle}
                        onChange={(e) => {
                          setTaxStateMent(e.target.files[0]);
                          handleFileChange(e);
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid size={12}>
                    <ContainedButton
                      variant="contained"
                      disabled={!taxNumber || !taxStateMent || isDisable}
                      onClick={() => handleSubmit()}
                    >
                      Submit
                    </ContainedButton>
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {/* Tier 3 Verified — show TIN */}
          {userDetails?.userTierDocsStatus === "1" && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FieldCard label="Tax Identification Number">
                  <Typography sx={fieldCardText}>{userDetails?.tin || "—"}</Typography>
                </FieldCard>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      <InfoPopUp
        open={show}
        onClose={onClose}
        title="Begin Tier 3 Verification"
        description="Before clicking on this button make sure that you have Bank Statement, Tax Identification Number, and Tax Statement."
      />
    </Box>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  p: { xs: 2.5, md: 3 },
};

const sectionHeader = {
  mb: 1.5,
};

const sectionTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const sectionCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.55,
  mt: 0.25,
};

const fieldCard = {
  backgroundColor: "var(--body-color-less2)",
  border: "1px solid rgba(125,125,125,0.13)",
  borderRadius: "12px",
  p: 2,
  height: "100%",
  minHeight: 100,
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

const fieldCardLabel = {
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "var(--text-color)",
  opacity: 0.45,
};

const fieldCardValue = {
  flex: 1,
  display: "flex",
  alignItems: "center",
};

const fieldCardText = {
  fontSize: "0.92rem",
  fontWeight: 600,
  color: "var(--text-color)",
  wordBreak: "break-word",
};

const fieldCardHint = {
  fontSize: "0.7rem",
  color: "var(--text-color)",
  opacity: 0.45,
  lineHeight: 1.5,
};

const dobChip = {
  px: 1.5,
  py: 0.75,
  borderRadius: "8px",
  backgroundColor: "var(--body-color)",
  border: "1px solid rgba(125,125,125,0.2)",
  fontSize: "0.9rem",
  fontWeight: 700,
  color: "var(--text-color)",
  minWidth: 40,
  textAlign: "center",
};

const uploadCard = {
  backgroundColor: "var(--body-color-less2)",
  border: "1px dashed rgba(125,125,125,0.3)",
  borderRadius: "12px",
  p: 2.5,
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

const uploadCardHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const uploadCardTitle = {
  fontSize: "0.88rem",
  fontWeight: 700,
  color: "var(--text-color)",
};

const uploadCardHint = {
  fontSize: "0.72rem",
  color: "var(--text-color)",
  opacity: 0.5,
  lineHeight: 1.5,
};

const fileInputStyle = {
  width: "100%",
  backgroundColor: "var(--body-color)",
  border: "1px solid rgba(125,125,125,0.25)",
  borderRadius: "8px",
  padding: "8px 10px",
  color: "var(--text-color)",
  fontSize: "0.82rem",
  cursor: "pointer",
};

const modernInputStyle = {
  width: "100%",
  padding: "10px 14px",
  border: "1px solid rgba(125,125,125,0.25)",
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  fontSize: "0.9rem",
  borderRadius: "10px",
  outline: "none",
  boxSizing: "border-box",
};

export default Tier3Document;
